import { createFileRoute, Link } from "@tanstack/react-router";
import { getGlossary, type GlossaryEntry } from "@/lib/glossary.functions";
import { TOPICS } from "@/lib/taxonomy";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Glossary | Canonical KAfrequency & Buddha KA Terminology";
const DESCRIPTION =
  "The canonical definitions used across the Buddha KA and KAfrequency ecosystem — Codex concepts and topic terminology, defined once and referenced everywhere.";

export const Route = createFileRoute("/glossary")({
  loader: async () => ({ entries: await getGlossary() }),
  component: GlossaryPage,
  head: ({ loaderData }) => {
    const entries = loaderData?.entries ?? [];
    const all = [
      ...TOPICS.map((t) => ({ name: t.name, description: t.definition, url: `${SITE_URL}/topics/${t.slug}` })),
      ...entries.map((e) => ({ name: e.term, description: e.definition, url: `${SITE_URL}${e.path}` })),
    ];
    return {
      ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/glossary" }),
      links: [{ rel: "canonical", href: absoluteUrl("/glossary") }],
      scripts: [
        ldScript(
          pageNode({
            type: "CollectionPage",
            id: `${SITE_URL}/glossary#glossary`,
            url: `${SITE_URL}/glossary`,
            name: "Glossary",
            description: DESCRIPTION,
            about: [personRef(), orgRef()],
            extra: {
              publisher: orgRef(),
              mainEntity: {
                "@type": "DefinedTermSet",
                name: "KAfrequency Glossary",
                url: `${SITE_URL}/glossary`,
                hasDefinedTerm: all.map((t) => ({
                  "@type": "DefinedTerm",
                  name: t.name,
                  description: t.description,
                  url: t.url,
                  inDefinedTermSet: `${SITE_URL}/glossary`,
                })),
              },
            },
          }),
        ),
        ldScript(breadcrumbs([["Glossary", "/glossary"]])),
      ],
    };
  },
});

function GlossaryPage() {
  const { entries } = Route.useLoaderData() as { entries: GlossaryEntry[] };

  return (
    <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Canonical Terminology</p>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl">Glossary</h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">{DESCRIPTION}</p>

      <h2 className="mt-16 font-serif text-2xl text-gold-luminous">Topics</h2>
      <dl className="mt-6 space-y-6">
        {TOPICS.map((t) => (
          <div key={t.slug} id={t.slug} className="rounded-xl border hairline bg-charcoal/40 p-5">
            <dt className="font-serif text-xl">
              <Link to="/topics/$slug" params={{ slug: t.slug }} className="hover:text-gold-luminous">
                {t.name}
              </Link>
            </dt>
            <dd className="mt-2 text-sm text-muted-foreground">{t.definition}</dd>
          </div>
        ))}
      </dl>

      <h2 className="mt-16 font-serif text-2xl text-gold-luminous">Codex concepts</h2>
      {entries.length === 0 ? (
        <p className="mt-6 text-muted-foreground">Codex entries are temporarily unavailable.</p>
      ) : (
        <dl className="mt-6 space-y-6">
          {entries.map((e) => (
            <div key={e.slug} id={e.slug} className="rounded-xl border hairline bg-charcoal/40 p-5">
              <dt className="font-serif text-xl">
                <Link to="/codex/$slug" params={{ slug: e.slug }} className="hover:text-gold-luminous">
                  {e.term}
                </Link>
                {e.cluster && (
                  <span className="ml-3 text-[10px] uppercase tracking-[0.25em] text-gold">{e.cluster}</span>
                )}
              </dt>
              {e.essence && <dd className="mt-1 text-sm text-ivory/80">{e.essence}</dd>}
              <dd className="mt-2 text-sm text-muted-foreground">{e.definition}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
